import type { Category, Platform, Provider } from "./types";

const retrievedAt = "2026-09-14";

export const categories: Category[] = [
  {
    slug: "electronic-games",
    name: "Electronic Games & Slots",
    deck: "Studios, themes and interface systems behind browser and mobile electronic games.",
    description: [
      "Electronic games cover a broad range of reel-based, arcade-like and rapid-play formats. INPAEZ separates the visual theme from the underlying product category and identifies the studio responsible for a title whenever an official source is available.",
      "Our platform observations currently connect this category with ATG, PG SOFT, Pragmatic Play, JDB, RSG, NetEnt, Red Tiger, Nolimit City and other catalogues visible across UTown and AT99 Vietnam. Availability is time-sensitive and is always described as an observation rather than a permanent promise."
    ]
  },
  {
    slug: "live-casino",
    name: "Live Casino",
    deck: "A factual guide to studios, streamed tables, classic games and localised interfaces.",
    description: [
      "Live casino products combine a video stream with a digital betting and information layer. A useful review therefore considers broadcast quality, table information, language support, rules access and the identity of the studio instead of focusing only on surface-level presentation.",
      "The client catalogues documented by INPAEZ include brands such as Evolution, Pragmatic Play, WM Casino and Dream Gaming. Individual tables may differ, so a provider relationship never implies that every named game is available on every platform."
    ]
  },
  {
    slug: "fishing-games",
    name: "Fishing Games",
    deck: "Visual, mechanical and provider-level analysis of fish-shooting entertainment.",
    description: [
      "Fishing games use moving targets, aiming controls and layered visual effects. They often sit between arcade and electronic-game taxonomies, so INPAEZ records both the platform category and the supplier's own classification when the two differ.",
      "AT99 Vietnam currently provides the strongest verified category map for this topic, including observed relationships with JDB, 77G, BT, RSG, MW and other suppliers."
    ]
  },
  {
    slug: "table-card-games",
    name: "Table & Card Games",
    deck: "Rules, terminology and product families across streamed and RNG-based formats.",
    description: [
      "This hub distinguishes live-hosted tables from software-based card and board games. Baccarat, roulette, blackjack, poker, mahjong and regional card formats can share familiar names while differing in interface, timing and optional rules.",
      "Every game article links back to its provider and platform evidence. Generic rules pages do not claim that a specific supplier or operator carries every variant discussed."
    ]
  }
];

export const providers: Provider[] = [
  {
    slug: "atg",
    name: "ATG Games",
    legalName: "Agile Titan Games",
    alternateNames: ["ATG", "ATG Electronic"],
    officialUrl: "https://atg-games.com/",
    categories: ["electronic-games", "table-card-games"],
    platforms: ["utown", "guanhao-entertainment", "at99-vietnam"],
    deck: "An Asia-facing game supplier associated with the Storm of Seth series and a catalogue spanning slots, card games, arcade formats and lottery products.",
    facts: [
      "The company's official About page identifies ATG as Agile Titan Games and describes it as an international iGaming game-development and solutions provider.",
      "ATG's official catalogue groups products under slots, card games, arcade games and lottery.",
      "The official catalogue visibly includes Storm of Seth 2 Awakening, Elder God Baphomet, Tiger Girl, Storm of Seth, Scarlet Three Kingdoms, Wuxia Caishen, Son Go Ku and several card or rapid-play titles."
    ],
    analysis: [
      {
        heading: "Portfolio identity",
        paragraphs: [
          "ATG's catalogue is especially useful for studying how familiar Asian and mythological subjects are adapted into contemporary browser games. Egyptian mythology anchors the Storm of Seth line, while Scarlet Three Kingdoms, Wuxia Caishen and Son Go Ku draw on stories and character types already recognisable across Chinese-speaking markets. That recurring thematic vocabulary gives the studio a clearer identity than a catalogue made from unrelated one-off designs.",
          "The supplier should not be reduced to slots alone. Its official product navigation also exposes card, arcade and lottery groupings. For INPAEZ, those categories provide a reliable structure for linking individual title pages to a broader provider profile without assuming that every operator carries the full ATG portfolio."
        ]
      },
      {
        heading: "Observed client-platform connections",
        paragraphs: [
          "ATG has the clearest presence across the three partner catalogues covered here. UTown names ATG as an electronic-game brand and references the Storm of Seth series. Guanhao includes direct ATG demonstration paths for ten named titles, while AT99 Vietnam has eight independently routed ATG game pages based on its owner-supplied catalogue.",
          "These observations establish that the same supplier can support several different editorial angles: a Traditional Chinese provider guide for UTown, an AI-visual and entertainment presentation angle for Guanhao, and a Vietnamese-market catalogue guide for AT99. They do not by themselves establish ownership, exclusivity or uninterrupted availability."
        ]
      },
      {
        heading: "Editorial assessment",
        paragraphs: [
          "ATG receives detailed coverage because official sources, named products and partner-platform catalogues overlap unusually well. Articles can compare recurring themes, localisation choices, mobile presentation and the difference between slot, poker and rapid-play interfaces. Any discussion of mathematical behaviour must cite the exact official game page and version rather than applying a portfolio-wide claim.",
          "Readers can continue through the electronic-games guide, table-and-card-games guide, the three platform profiles and relevant title coverage. These links keep factual studio information separate from commercial platform placement."
        ]
      }
    ],
    verifiedGames: ["Storm of Seth 2 Awakening", "Storm of Seth", "Elder God Baphomet", "Tiger Girl", "Scarlet Three Kingdoms", "Wuxia Caishen", "Son Go Ku", "Mahjong", "Banker Bull", "Casino Bull"],
    sources: [
      { title: "About Agile Titan Games", url: "https://atg-games.com/about-us", publisher: "ATG Games", retrievedAt },
      { title: "ATG Games product catalogue", url: "https://atg-games.com/zh-cn/games", publisher: "ATG Games", retrievedAt }
    ],
    updatedAt: retrievedAt
  },
  {
    slug: "pg-soft",
    name: "PG SOFT",
    legalName: "Pocket Games Soft",
    alternateNames: ["PGsoft", "PG Soft", "PG"],
    officialUrl: "https://www.pgsoft.com/",
    categories: ["electronic-games"],
    platforms: ["utown", "at99-vietnam"],
    deck: "A mobile-oriented digital game studio whose official catalogue emphasises visual production, cross-device delivery and localised releases.",
    facts: [
      "PG SOFT's official company page states that the brand is also known as Pocket Games Soft and was founded in 2015.",
      "The company describes a multidisciplinary team covering design, art, animation, production, mathematics, software and audio.",
      "Its official catalogue exposes individual games with fields for release date, volatility, RTP, maximum win, languages and certification, although values must be checked title by title."
    ],
    analysis: [
      {
        heading: "Mobile-first presentation",
        paragraphs: [
          "PG SOFT's public identity is built around mobile game presentation rather than a simple list of reel titles. The company emphasises art direction, animation, sound and browser-to-device delivery. That makes the brand relevant to INPAEZ's analysis of how short sessions, portrait-oriented interfaces and cinematic transitions influence game discovery in Asian mobile markets.",
          "A provider profile should distinguish the corporate story from the attributes of an individual game. The official catalogue offers filters and title-level technical information, but those values can change by jurisdiction or release. INPAEZ therefore records each value only on the corresponding game page and includes a retrieval date."
        ]
      },
      {
        heading: "Observed client-platform connections",
        paragraphs: [
          "UTown's current content model lists PG as an electronic-game provider and identifies Mahjong Ways as a notable title. AT99 Vietnam also lists PGsoft in its slots catalogue. This supports a dated connection on both platform guides, but does not mean their game selections are identical.",
          "The labels `PG` and `PGsoft` both lead to the official display name `PG SOFT`. Keeping these spellings together on one canonical brand page prevents duplicate guides and gives readers one reliable place to find the studio's background, games and sources."
        ]
      },
      {
        heading: "Editorial assessment",
        paragraphs: [
          "The most useful PG SOFT coverage is product-led: title art, mobile interaction, localisation, audio and documented mechanics. Broader claims such as popularity or market leadership require independent measurement and should not be inferred from promotional copy. Recommendation articles may mention PG SOFT only after explaining the selection criteria and linking to this sourced profile.",
          "The provider page should link to the electronic-games hub, UTown and AT99 Vietnam profiles, and verified PG SOFT titles. It should also link laterally to ATG and Pragmatic Play only where a comparison has a clear dimension such as mobile layout or catalogue organisation."
        ]
      }
    ],
    verifiedGames: ["Mahjong Ways"],
    sources: [
      { title: "About PG SOFT", url: "https://www.pgsoft.com/company/", publisher: "PG SOFT", retrievedAt },
      { title: "PG SOFT all games", url: "https://www.pgsoft.com/games/all/", publisher: "PG SOFT", retrievedAt }
    ],
    updatedAt: retrievedAt
  },
  {
    slug: "pragmatic-play",
    name: "Pragmatic Play",
    alternateNames: ["PP", "PP Live"],
    officialUrl: "https://www.pragmaticplay.com/",
    categories: ["electronic-games", "live-casino"],
    platforms: ["utown", "at99-vietnam"],
    deck: "A multi-product supplier with official verticals spanning slots, live casino, bingo, virtual sports and sportsbook content.",
    facts: [
      "Pragmatic Play's official site describes a multi-product portfolio delivered through a single API.",
      "The official Live Casino section identifies studio production, multi-camera video, HTML5 interfaces and multilingual localisation among its product features.",
      "The live portfolio lists classic and adapted formats including baccarat, blackjack, roulette and sic bo alongside branded game-show products."
    ],
    analysis: [
      {
        heading: "A multi-vertical provider",
        paragraphs: [
          "Pragmatic Play spans more than one product line. Describing it only as a slot studio would omit its live casino, bingo and sports-related products, so this guide connects the brand to several game categories while keeping each title with its official product line.",
          "Its Live Casino material offers useful factual dimensions for analysis: production studios, video delivery, interface localisation and classic table families. Marketing language is not converted into an objective quality score. Instead, the site explains what the provider says it offers and what can be observed in the relevant client catalogue."
        ]
      },
      {
        heading: "Observed client-platform connections",
        paragraphs: [
          "UTown identifies PP Live as a live-casino provider and also references Pragmatic Play in broader brand copy. AT99 Vietnam records PP within the slots category. Because the abbreviation can be confused with PG SOFT, INPAEZ uses the full Pragmatic Play name wherever the source supports that match.",
          "This cross-category relationship supports separate articles on Pragmatic Play slots and live products. It does not justify claiming that every product vertical is available on either client platform, and each platform statement needs its own observation date."
        ]
      },
      {
        heading: "Editorial assessment",
        paragraphs: [
          "Pragmatic Play is valuable for comparative content because one official supplier architecture covers several formats. Editorial pages can examine how a single brand presents slots and live tables, or how localisation differs between product verticals. Any title-level mechanic or numerical property must come from that title's official page.",
          "Internal links should connect this profile to both live-casino and electronic-games hubs, then to UTown and AT99 Vietnam. Recommendation placement is permitted only after INPAEZ publishes its methodology and commercial disclosure."
        ]
      }
    ],
    verifiedGames: ["Gates of Olympus Roulette", "Amazing Baccarat", "Mega Roulette", "Blackjack X", "Mega Sic Bac"],
    sources: [
      { title: "Pragmatic Play corporate site", url: "https://www.pragmaticplay.com/", publisher: "Pragmatic Play", retrievedAt },
      { title: "Pragmatic Play Live Casino", url: "https://www.pragmaticplay.com/en/live-casino/", publisher: "Pragmatic Play", retrievedAt }
    ],
    updatedAt: retrievedAt
  },
  {
    slug: "jdb-gaming",
    name: "JDB Gaming",
    alternateNames: ["JDB", "JDB Electronic"],
    officialUrl: "https://www.jdbgaming.com/",
    categories: ["electronic-games", "fishing-games", "table-card-games"],
    platforms: ["utown", "at99-vietnam"],
    deck: "A supplier whose official catalogue spans slots, fish-shooting, card and board games, arcade products and bingo.",
    facts: [
      "JDB's official games page groups its catalogue into arcade, fish-shooting, slot and other product families.",
      "The supplier describes a unified API and backend-management approach for operator integration.",
      "AT99's verified dataset names five JDB fishing titles, while UTown identifies JDB with electronic and fishing content."
    ],
    analysis: [
      {
        heading: "Cross-category catalogue structure",
        paragraphs: [
          "JDB is particularly useful for explaining why provider taxonomies should not be one-dimensional. Its official catalogue spans reel-based products, fish-shooting, cards, boards, arcade formats and bingo. On a platform, however, the same provider may be displayed under only a subset of those categories.",
          "INPAEZ records the supplier's official portfolio separately from the categories observed on a client site. That distinction prevents a provider-level fact from being misrepresented as current operator availability. It also creates accurate internal paths between JDB, fishing games and electronic games."
        ]
      },
      {
        heading: "Observed client-platform connections",
        paragraphs: [
          "UTown's content model names JDB as an electronic provider associated with treasure and fishing content. AT99 Vietnam maps JDB to slots, fishing games and card games, and its curated data lists Dragon Fishing II, Dragon Fishing, Cai Shen Fishing, Fishing YiLuFa and Five Dragons Fishing.",
          "Those named titles are strong candidates for individual pages because both the platform record and the official supplier catalogue can be checked. Before publication, spelling and title versions must be matched to the current official JDB page."
        ]
      },
      {
        heading: "Editorial assessment",
        paragraphs: [
          "JDB coverage should focus on navigation, genre boundaries, visual density and the relationship between fishing and arcade interfaces. It should not imply that energetic animation changes mathematical outcomes. Where technical integration is discussed, the wording should stay close to JDB's official description and avoid unverified performance claims.",
          "The profile connects naturally to the fishing-games, electronic-games and table-card-games hubs, as well as the UTown and AT99 platform pages. Comparison articles should use explicit dimensions rather than ranking providers by unsupported popularity."
        ]
      }
    ],
    verifiedGames: ["JDB Dragon Fishing II", "JDB Dragon Fishing", "JDB Cai Shen Fishing", "JDB Fishing YiLuFa", "JDB Five Dragons Fishing"],
    sources: [
      { title: "JDB official game catalogue", url: "https://www.jdbgaming.com/en/games?lang=en-US", publisher: "JDB Gaming", retrievedAt }
    ],
    updatedAt: retrievedAt
  },
  {
    slug: "evolution",
    name: "Evolution",
    alternateNames: ["Evolution Gaming", "EVO Casino"],
    officialUrl: "https://www.evolution.com/",
    categories: ["live-casino", "electronic-games"],
    platforms: ["utown", "at99-vietnam"],
    deck: "A live-casino and interactive-entertainment supplier whose group portfolio includes several distinct live and slot brands.",
    facts: [
      "Evolution's official brand directory separates Evolution from group brands including Ezugi, NetEnt, Red Tiger, Nolimit City and Big Time Gaming.",
      "The Evolution brand page describes classic live tables, live game shows and First Person RNG products.",
      "Official examples include roulette, blackjack, baccarat, dragon tiger, sic bo and poker variants, plus game-show formats."
    ],
    analysis: [
      {
        heading: "Brand and group must be separated",
        paragraphs: [
          "Evolution shows why brand relationships need careful wording. The group contains several distinct brands, but NetEnt, Red Tiger and Nolimit City are not alternate names for Evolution. Each studio keeps its own identity while official evidence explains the group relationship.",
          "The namesake Evolution brand is centred on live casino, game shows and First Person RNG presentations. This makes its provider page broader than a simple list of baccarat or roulette tables and allows factual discussion of studio production, localisation and the transition between live and RNG interfaces."
        ]
      },
      {
        heading: "Observed client-platform connections",
        paragraphs: [
          "UTown's current public template names Evolution among its live-casino providers and separately mentions NetEnt, Red Tiger and Nolimit City in electronic-game contexts. AT99 Vietnam records EVO Casino under Live Casino while NetEnt, Red Tiger and other group brands appear separately under Slots.",
          "INPAEZ keeps these observations attached to the correct brand. The platform label `EVO Casino` can guide readers to Evolution, but it does not replace the official name. The group relationship comes from Evolution's official brand directory."
        ]
      },
      {
        heading: "Editorial assessment",
        paragraphs: [
          "Evolution supports high-value educational content around live production, classic table adaptation and game-show formats. Articles should explain visible product differences and official portfolio structure rather than repeating claims of market leadership as editorial fact.",
          "This page links to the live-casino hub, the UTown and AT99 profiles, and later to separate NetEnt, Red Tiger and Nolimit City pages. That architecture lets search engines see both the parent-group relationship and the independence of each product brand."
        ]
      }
    ],
    verifiedGames: ["Roulette", "Blackjack", "Baccarat", "Dragon Tiger", "Super Sic Bo", "Crazy Time", "Mega Ball", "Dream Catcher"],
    sources: [
      { title: "Evolution brand portfolio", url: "https://www.evolution.com/brands", publisher: "Evolution", retrievedAt },
      { title: "Evolution live casino and game shows", url: "https://www.evolution.com/brands/evolution", publisher: "Evolution", retrievedAt }
    ],
    updatedAt: retrievedAt
  }
];

export const platforms: Platform[] = [
  {
    slug: "utown",
    name: "UTown",
    market: "Traditional Chinese-language audiences",
    language: "Traditional Chinese",
    categories: ["live-casino", "electronic-games", "table-card-games"],
    providers: ["atg", "pg-soft", "pragmatic-play", "jdb-gaming", "evolution"],
    description: [
      "UTown is one of the Asian platforms covered by INPAEZ. Its current content menu includes live casino, electronic games, sports and table or card games.",
      "Provider placement is based on the owner-managed BH catalogue. INPAEZ presents it as a dated catalogue observation and links readers to provider profiles for independently sourced background.",
      "The platform's editorial material also addresses USDT and digital-payment topics for its Traditional Chinese audience. Those educational topics are kept separate from game-supplier identity and do not represent financial advice or a promise that every payment method is available to every reader.",
      "INPAEZ maintains a commercial content relationship with UTown. The relationship supports catalogue documentation, while claims about third-party studios remain subject to official-source verification and a visible observation date."
    ]
  },
  {
    slug: "guanhao-entertainment",
    name: "Guanhao Entertainment",
    market: "Traditional Chinese-language audiences",
    language: "Traditional Chinese",
    categories: ["live-casino", "electronic-games", "table-card-games"],
    providers: ["atg"],
    description: [
      "Guanhao Entertainment combines a four-category entertainment lobby with an editorial project covering game culture and AI-created visual media.",
      "Its current supplier relationship is deliberately conservative: only ATG demonstration links have enough local and official evidence for a named provider connection. Other lobby cards remain presentation topics until the owner confirms their formal product names.",
      "AI-assisted game imagery and promotional video form a documented part of the brand's media direction. INPAEZ discusses how the media was produced separately from how a game works, and does not guess a supplier from generated artwork.",
      "The Guanhao relationship is commercial and is disclosed. Named-game claims are published only after the owner mapping and an official provider source agree; availability is never presented as permanent."
    ]
  },
  {
    slug: "at99-vietnam",
    name: "AT99 Vietnam",
    market: "Vietnam",
    language: "Vietnamese",
    categories: ["electronic-games", "live-casino", "fishing-games", "table-card-games"],
    providers: ["atg", "pg-soft", "pragmatic-play", "jdb-gaming", "evolution"],
    description: [
      "AT99 Vietnam is the Vietnamese-language platform in the current INPAEZ guide. Its broader menu also includes sports, lottery, cockfighting and lô tô content.",
      "English INPAEZ stories can introduce this Vietnamese catalogue while clearly labelling the destination language. The market context remains useful to international readers without implying identical service elsewhere.",
      "According to the owner-provided corporate statement, the AT99 brand originated in Taiwan and has formally entered the Vietnamese market. Its official web presence provides Vietnamese, Chinese and English language access, while at99asia.com serves as the official Vietnamese SEO information entry.",
      "INPAEZ discloses its commercial content relationship with AT99. The platform profile documents market language, category structure and confirmed provider mappings without converting those observations into licensing, exclusivity or outcome claims."
    ]
  }
];

export const providerBySlug = new Map(providers.map((provider) => [provider.slug, provider]));
export const categoryBySlug = new Map(categories.map((category) => [category.slug, category]));
export const platformBySlug = new Map(platforms.map((platform) => [platform.slug, platform]));
